"use client";

/**
 * Activity Signup Modal
 * Public-facing modal for registering or joining the waitlist for an activity.
 * On `invited` landing pages with a logged-in viewer, identity is server-resolved
 * and the form skips the email/name/phone rows. Anonymous public-page visitors
 * still see the full form.
 */

import type React from "react";
import { useCallback, useMemo, useState } from "react";
import { AlertCircle, Calendar, CheckCircle2, MapPin } from "lucide-react";
import { usePostHog } from "posthog-js/react";
import FetchModal from "@/fetch-ui/FetchModal";
import { PhoneNumberInput } from "@/components/ui/shared/PhoneNumberInput";
import { toE164 } from "@/lib/phone/phone-utils";
import { getButtonTextColor } from "@/features/landing-pages/_logic/color-utils";
import type { ActivityRow, ActivityTimeSlotRow } from "../../_logic/types/db-types";
import { toggleArrayItem } from "../../_logic/utils/toggle-array-item";
import type { RuleBlockReason } from "../../_logic/utils/activity-signup-status";
import { TimeSlotPicker, type SlotPickerState } from "./TimeSlotPicker";
import { CustomFieldInput } from "./CustomFieldInput";
import { PlusOneGuestsInput } from "./PlusOneGuestsInput";
import { SignupSuccessView } from "./SignupSuccessView";
import type { SignupMode } from "./SignupSuccessView";
import type { SlotRegistrationCounts } from "../../_logic/actions/signup-actions";
import {
  registerForActivity,
  joinActivityWaitlist,
  registerForActivityFromSession,
} from "../../_logic/actions/signup-actions";
import type { CalendarSlotInfo } from "./CalendarAddButtons";
import { isValidEmail } from "@/lib/validation/email";
import { usePublicViewContext } from "./PublicViewContext";
import { SanitizedHtml } from "../canvas/renderers/_shared/SanitizedHtml";
import { loadAttendeePrefill, saveAttendeePrefill } from "../../_logic/utils/attendee-prefill-storage";
import { ConfirmChangesModal } from "./ConfirmChangesModal";
import { formatSlotDisplayLabel } from "../../_logic/utils/time-slot-utils";
import { useToast } from "@/components/providers/FetchToastProvider";
import { useActivityReconcile } from "../../_hooks/useActivityReconcile";
import { useActivityManageDiff } from "../../_hooks/useActivityManageDiff";
import type { RegisterPromiseResult, RegisterVerdict } from "../../_logic/utils/activity-register-results";
import {
  succeededSlotIdsFrom,
  tallyRegisterResults,
  sendConsolidatedEmailsForBatch,
  withOneRetryOnRejection,
} from "../../_logic/utils/activity-register-results";
import { REGISTRATION_CONNECTION_ISSUE_MESSAGE } from "../../_logic/constants";

export interface ActivitySignupModalProps {
  activity: ActivityRow;
  timeSlots: ActivityTimeSlotRow[];
  slotCounts: Record<number, SlotRegistrationCounts>;
  primaryColor: string;
  /** Landing page id stamped onto the registration row so emails know which template set to use. */
  landingPageId: number;
  /**
   * Client-side day-cap/exclusion-group hint, from useActivityRuleState. Optional and fails safe
   * when omitted (no hint shown, nothing silently allowed — the server-side check at signup time
   * is authoritative regardless). Wired by ActivitiesRenderer and ScheduleDetailModal.tsx, both
   * public-view surfaces where the browsing viewer's own registrations are known ahead of the
   * modal opening. Not wired by the two admin "Add Signup" call sites (SignupsView.tsx,
   * ActivityListView.tsx) — there the attendee being registered is an arbitrary email typed into
   * this modal's own form, so no viewer identity exists to compute the hint from before the modal
   * is even open.
   */
  ruleBlock?: {
    /** Per-slot-id reason, passed through to TimeSlotPicker. */
    bySlotId: Map<number, RuleBlockReason>;
    /**
     * Specific explanation for why every bookable slot is blocked (from describeRuleBlock), shown
     * as a red callout above the (still-visible, still-disabled) time slot list. Only ActivitiesRenderer
     * computes this — the one caller with the full activities list needed to name the conflicting
     * activity/activities. Omitted (not just empty) elsewhere, so the callout stays hidden.
     */
    message?: string;
  };
  onClose: () => void;
  /**
   * Called after a signup or manage reconcile succeeds (fully or partially). `addedSlotIds` are
   * slots newly registered/waitlisted; `cancelledSlotIds` are slots whose registration was
   * cancelled. The parent uses these to optimistically update its already-signed-up state +
   * registration map, then refresh counts. For a plain new signup `cancelledSlotIds` is empty.
   */
  onSuccess: (params: { activityId: number; addedSlotIds: number[]; cancelledSlotIds: number[] }) => void;
}

type ModalState = "form" | "submitting" | "review" | "success" | "error";

/**
 * Fired when a register/waitlist call rejects on both the initial attempt and the retry — i.e.
 * the request never got a server-returned answer (network drop, timeout). Previously invisible
 * to analytics entirely: only the generic fallback copy showed, with nothing logged or captured.
 */
const REGISTRATION_CLIENT_FAILURE_EVENT = "activity_registration_client_failure";

/** True when any of the first `count` guest-name slots is blank/whitespace. */
function hasBlankGuestName(names: string[], count: number): boolean {
  for (let i = 0; i < count; i++) {
    if ((names[i] ?? "").trim().length === 0) return true;
  }
  return false;
}

/**
 * Pull calendar info out of the results array for the single-slot confirmed-registration
 * case. Returns undefined for any other shape (multi-slot, waitlist, failure).
 */
function pickSingleSlotCalendarInfo(
  results: RegisterPromiseResult[],
  registered: number
): CalendarSlotInfo | undefined {
  if (results.length !== 1 || registered !== 1) return undefined;
  const first = results[0];
  if (first?.status !== "fulfilled" || !first.value.success || first.value.waitlisted) return undefined;
  return first.value.slotInfo;
}

/**
 * Modal for signing up or joining the waitlist for an activity.
 *
 * When `usePublicViewContext().viewer` is non-null (logged-in viewer on an
 * `invited` page), identity comes from the server: the email/firstName/lastName
 * rows are hidden, and the phone row is only shown when the viewer doesn't have
 * a phone on file. Submission goes through `registerForActivityFromSession`.
 *
 * Anonymous public-page visitors see the full form and submit via
 * `registerForActivity` / `joinActivityWaitlist`.
 *
 * @param props - See ActivitySignupModalProps.
 * @returns The signup modal.
 */
export function ActivitySignupModal({
  activity,
  timeSlots,
  slotCounts,
  primaryColor,
  landingPageId,
  ruleBlock,
  onClose,
  onSuccess,
}: ActivitySignupModalProps): React.ReactElement {
  // Hooks
  const buttonTextColor = getButtonTextColor(primaryColor);
  const { viewer, eventId, signedUpSlotIds, waitlistedSlotIds, registrationBySlotId, timeZone } =
    usePublicViewContext();
  const { showToast } = useToast();
  const posthog = usePostHog();

  // Derived values
  // When a viewer is server-resolved (logged in), name/email stay hidden but
  // we still need a phone field when the profile has no phone on file.
  const isLoggedIn = viewer !== null;
  const needsIdentityInputs = !isLoggedIn;
  const needsPhoneInput = !isLoggedIn || !viewer?.phoneNumber;

  // States — identity prefilled from logged-in viewer when present (read-only for the form;
  // the server re-resolves identity for the session-based path). Anonymous visitors
  // get their previous-signup info prefilled from per-event localStorage so they
  // don't re-type name/email/phone for every activity.
  const [initialPrefill] = useState(() => (!isLoggedIn && eventId ? loadAttendeePrefill(eventId) : null));
  const [mode, setMode] = useState<SignupMode>("register");
  const [modalState, setModalState] = useState<ModalState>("form");
  const [errorMessage, setErrorMessage] = useState("");
  const [partialFailure, setPartialFailure] = useState<
    { succeeded: number; total: number; reason?: string } | undefined
  >(undefined);
  const [multiSlotBreakdown, setMultiSlotBreakdown] = useState<{ registered: number; waitlisted: number } | undefined>(
    undefined
  );
  const [calendarSlotInfo, setCalendarSlotInfo] = useState<CalendarSlotInfo | undefined>(undefined);
  // Seed the selection to the viewer's current registrations so the modal opens as an editor of
  // what they hold (untick to cancel, tick to add). Empty for a fresh signup → unchanged behavior.
  const [selectedSlotIds, setSelectedSlotIds] = useState<number[]>(() =>
    timeSlots.filter((s) => s.is_bookable && registrationBySlotId.has(s.id)).map((s) => s.id)
  );
  const [firstName, setFirstName] = useState(initialPrefill?.firstName ?? "");
  const [lastName, setLastName] = useState(initialPrefill?.lastName ?? "");
  const [email, setEmail] = useState(initialPrefill?.email ?? "");
  const [phoneDigits, setPhoneDigits] = useState(initialPrefill?.phoneDigits ?? "");
  const [countryDialCodeId, setCountryDialCodeId] = useState<number | null>(initialPrefill?.countryDialCodeId ?? null);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [phoneError, setPhoneError] = useState<string | null>(null);
  const [dietaryRestrictions, setDietaryRestrictions] = useState("");
  const [customFieldValues, setCustomFieldValues] = useState<Record<string, string | boolean>>({});
  // "+1 guests": guestCount drives how many name fields show; guestNames keeps typed names by
  // position (length may exceed guestCount so lowering the count doesn't drop earlier entries).
  const [guestCount, setGuestCount] = useState(0);
  const [guestNames, setGuestNames] = useState<string[]>([]);
  const [guestErrorsShown, setGuestErrorsShown] = useState(false);

  // Derived values — slot selection
  const bookableSlots = useMemo(() => timeSlots.filter((s) => s.is_bookable), [timeSlots]);

  // Push the seeded existing guests into the form's guest fields. Stable so the manage-diff hook's
  // seed effect doesn't re-fire on every render.
  const handleGuestsSeeded = useCallback((names: string[]) => {
    setGuestCount(names.length);
    setGuestNames(names);
  }, []);

  // Manage/edit diff + "+1 guests" baseline. Owns the held-slot set, the add/cancel/guest diff, the
  // won't-fit advisory, and the one-time guest seed. Non-empty only for session-proven viewers, so
  // the whole edit/cancel path is gated to them; anonymous viewers fall through to the unchanged
  // signup behavior (empty set → no diff to cancel, picker rows stay disabled).
  const {
    registeredSlotIds,
    canManage,
    needsGuestSeed,
    toAddSlotIds,
    toCancelSlotIds,
    guestsChanged,
    guestUpdateSlotIds,
    hasChanges,
    wontFitWarning,
    guestSeedStatus,
    applyGuestBaseline,
  } = useActivityManageDiff({
    activity,
    bookableSlots,
    slotCounts,
    registrationBySlotId,
    selectedSlotIds,
    guestCount,
    guestNames,
    showToast,
    onGuestsSeeded: handleGuestsSeeded,
  });
  const editableRegisteredSlotIds = canManage ? registeredSlotIds : undefined;

  // Human-readable slot labels keyed by id, for the REVIEW summary dialog.
  const slotLabelById = useMemo(() => {
    const map = new Map<number, string>();
    for (const s of bookableSlots) {
      map.set(s.id, formatSlotDisplayLabel(s, timeZone));
    }
    return map;
  }, [bookableSlots, timeZone]);
  const toAddLabels = useMemo(
    () => toAddSlotIds.map((id) => slotLabelById.get(id) ?? "Selected time"),
    [toAddSlotIds, slotLabelById]
  );
  const toCancelLabels = useMemo(
    () => toCancelSlotIds.map((id) => slotLabelById.get(id) ?? "Your time"),
    [toCancelSlotIds, slotLabelById]
  );
  // Labels of the slots the viewer is registered for — shown in the anonymous "already signed up"
  // view so they can see which time(s) they chose (they cancel via the email link).
  const registeredSlotLabels = useMemo(
    () =>
      bookableSlots
        .filter((s) => signedUpSlotIds.has(s.id))
        .map((s) => slotLabelById.get(s.id) ?? formatSlotDisplayLabel(s, timeZone)),
    [bookableSlots, signedUpSlotIds, slotLabelById, timeZone]
  );

  const availableSlots = useMemo(() => {
    if (mode === "waitlist") {
      return bookableSlots.filter((s) => {
        const counts = slotCounts[s.id];
        return counts && s.capacity !== null && counts.registered >= s.capacity;
      });
    }
    return bookableSlots;
  }, [bookableSlots, slotCounts, mode]);

  const allSlotsFull = useMemo(() => {
    return bookableSlots.every((s) => {
      const counts = slotCounts[s.id];
      return counts && s.capacity !== null && counts.registered >= s.capacity;
    });
  }, [bookableSlots, slotCounts]);

  const customFields = useMemo(() => activity.custom_fields ?? [], [activity.custom_fields]);

  const isMultiSelect = activity.allow_multiple_signups && mode === "register";

  // When the activity's restriction mode would block a duplicate same-slot signup, surface
  // the viewer's already-registered slot IDs so TimeSlotPicker can grey them out + label
  // them "Already registered". Fires whenever the server would reject a same-slot
  // re-attempt: explicit multi_choice_* restriction modes OR the implicit one-per-slot
  // enforced by allow_multiple_signups=false under mode=none. single_choice prevents the
  // modal from opening at all via ActivityCard's "Signed up" CTA state.
  const alreadyRegisteredSlotIds = useMemo(() => {
    const mode = activity.signup_restriction_mode ?? "none";
    const blocksDuplicateSlot =
      mode === "multi_choice_restricted" ||
      mode === "multi_choice_sign_again" ||
      (mode === "none" && !activity.allow_multiple_signups);
    return blocksDuplicateSlot ? signedUpSlotIds : undefined;
  }, [activity.signup_restriction_mode, activity.allow_multiple_signups, signedUpSlotIds]);

  // True when the viewer is already registered for every bookable slot — there's nothing left to
  // sign up for, so we show an "already registered" state instead of a dead form.
  const allSlotsAlreadyRegistered = useMemo(() => {
    if (!alreadyRegisteredSlotIds || bookableSlots.length === 0) return false;
    return bookableSlots.every((s) => alreadyRegisteredSlotIds.has(s.id));
  }, [bookableSlots, alreadyRegisteredSlotIds]);

  // Combines the 4 separate per-slot lookups above into one map for TimeSlotPicker, built once
  // here instead of threading each collection through as its own prop.
  const slotStates = useMemo(() => {
    // Manage mode: registeredCountByDate is a snapshot that doesn't account for slots queued for
    // cancellation in this same edit, so a viewer at their day cap could be shown as blocked from
    // swapping into a freed slot. The hint is advisory only (the server is authoritative
    // regardless) — simplest safe fix is to not show it while managing an existing registration.
    const effectiveRuleBlockBySlotId = canManage ? undefined : ruleBlock?.bySlotId;
    const map = new Map<number, SlotPickerState>();
    for (const slot of availableSlots) {
      map.set(slot.id, {
        counts: slotCounts[slot.id],
        isAlreadyRegistered: alreadyRegisteredSlotIds?.has(slot.id),
        isEditable: editableRegisteredSlotIds?.has(slot.id),
        isWaitlisted: waitlistedSlotIds?.has(slot.id),
        ruleBlock: effectiveRuleBlockBySlotId?.get(slot.id),
      });
    }
    return map;
  }, [
    availableSlots,
    slotCounts,
    alreadyRegisteredSlotIds,
    editableRegisteredSlotIds,
    waitlistedSlotIds,
    ruleBlock,
    canManage,
  ]);

  // Handlers
  const handleCustomFieldChange = useCallback((fieldId: string, value: string | boolean) => {
    setCustomFieldValues((prev) => ({ ...prev, [fieldId]: value }));
  }, []);

  const handleGuestNameChange = useCallback((index: number, name: string) => {
    setGuestNames((prev) => {
      const next = prev.slice();
      next[index] = name;
      return next;
    });
  }, []);

  const handleToggleSlot = useCallback(
    (slotId: number) => {
      setSelectedSlotIds((prev) => (isMultiSelect ? toggleArrayItem(prev, slotId) : [slotId]));
    },
    [isMultiSelect]
  );

  /** Drop every registered slot → opens the REVIEW step as a cancel-only change. */
  const handleCancelAll = useCallback(() => {
    setSelectedSlotIds([]);
    setModalState("review");
  }, []);

  const handleReconcileStart = useCallback(() => {
    setErrorMessage("");
    setPartialFailure(undefined);
  }, []);
  const handlePartialFailure = useCallback(
    ({
      reconciledSlotIds,
      errorMessage,
      guestBaseline,
    }: {
      reconciledSlotIds: number[];
      errorMessage: string;
      guestBaseline?: string[];
    }) => {
      setSelectedSlotIds(reconciledSlotIds);
      // Re-baseline guests only when the reconcile re-synced committed state, so the next confirm
      // diffs against what's actually stored rather than the stale pre-failure baseline.
      if (guestBaseline) applyGuestBaseline(guestBaseline);
      setErrorMessage(errorMessage);
      setModalState("error");
    },
    [applyGuestBaseline]
  );

  // Manage/edit reconcile (register adds + cancel removes). `isReconciling` is separate from
  // `modalState` so the REVIEW dialog can show its own spinner while the form stays mounted.
  const { isReconciling, runReconcile } = useActivityReconcile({
    activity,
    landingPageId,
    viewer,
    phoneDigits,
    countryDialCodeId,
    dietaryRestrictions,
    customFieldValues,
    guestCount,
    guestNames,
    registrationBySlotId,
    registeredSlotIds,
    onSuccess,
    onClose,
    showToast,
    onReconcileStart: handleReconcileStart,
    onPartialFailure: handlePartialFailure,
  });

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();

      // Manage/edit path (session viewer already holds ≥1 slot of this activity): submit applies a
      // diff. Validate only the additions, gate destructive removals behind the REVIEW step.
      if (canManage) {
        if (!hasChanges) return;
        // Phone + custom fields only gate *new* registrations; guest-only edits reuse the existing row.
        if (toAddSlotIds.length > 0) {
          if (needsPhoneInput && phoneDigits.trim()) {
            const formatted = toE164(phoneDigits, countryDialCodeId);
            if (!formatted) {
              setPhoneError("Please enter a valid phone number");
              return;
            }
          }
          setPhoneError(null);
          for (const field of customFields) {
            if (field.required) {
              const val = customFieldValues[field.id];
              if (val === undefined || val === "" || val === false) {
                setErrorMessage(`${field.label} is required`);
                setModalState("error");
                return;
              }
            }
          }
        }
        // Guest names must be filled whenever guests are being written — adding a slot OR editing
        // the kept registrations' guests.
        if (activity.allow_plus_one && guestCount > 0 && (toAddSlotIds.length > 0 || guestsChanged)) {
          if (hasBlankGuestName(guestNames, guestCount)) {
            setGuestErrorsShown(true);
            return;
          }
        }
        // Removing a slot is destructive → confirm first (guest edits ride along with the confirm).
        if (toCancelSlotIds.length > 0) {
          setModalState("review");
          return;
        }
        await runReconcile(toAddSlotIds, [], guestUpdateSlotIds);
        return;
      }

      if (selectedSlotIds.length === 0) return;

      // Validate identity inputs when they're shown (anonymous public-page path).
      if (needsIdentityInputs) {
        if (!isValidEmail(email.trim())) {
          setEmailError("Please enter a valid email address");
          return;
        }
        setEmailError(null);
      }

      if (needsPhoneInput && phoneDigits.trim()) {
        const formatted = toE164(phoneDigits, countryDialCodeId);
        if (!formatted) {
          setPhoneError("Please enter a valid phone number");
          return;
        }
      }
      setPhoneError(null);

      // Validate required custom fields
      for (const field of customFields) {
        if (field.required && mode === "register") {
          const val = customFieldValues[field.id];
          if (val === undefined || val === "" || val === false) {
            setErrorMessage(`${field.label} is required`);
            setModalState("error");
            return;
          }
        }
      }

      // Validate "+1 guests": every shown guest name must be filled in. Surfaced as inline
      // per-field errors (via guestErrorsShown) rather than the top banner, which is reserved
      // for server-returned failures that have no field to attach to.
      if (activity.allow_plus_one && guestCount > 0) {
        if (hasBlankGuestName(guestNames, guestCount)) {
          setGuestErrorsShown(true);
          return;
        }
      }

      // Guests are sliced to the selected count (later names are retained in state but not sent).
      const guestNamesPayload = activity.allow_plus_one && guestCount > 0 ? guestNames.slice(0, guestCount) : undefined;

      setModalState("submitting");
      setErrorMessage("");
      // Clear verdict-derived state so a follow-on submit can't show stale slot info
      // (e.g., a single-slot success followed by a multi-slot or waitlist resubmit).
      setCalendarSlotInfo(undefined);
      setPartialFailure(undefined);
      setMultiSlotBreakdown(undefined);

      // Multi-slot signups suppress per-call emails and send a single consolidated
      // confirmation with all slot .ics attachments after the inserts succeed.
      const isMultiSlot = selectedSlotIds.length > 1;

      // Snapshot the form identity once. applyVerdict (hoisted below) closes
      // over this, so it has to be initialized before either submit branch
      // calls applyVerdict — otherwise the logged-in branch hits a TDZ
      // ReferenceError and the modal stays stuck on "Submitting".
      const identityToRemember = {
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        email: email.trim(),
        phoneDigits: phoneDigits.trim(),
        countryDialCodeId,
      };

      // Session-based path: server has the identity; we only pass slot + extras + optional phone override.
      if (isLoggedIn) {
        const phoneOverride = !viewer.phoneNumber && phoneDigits.trim() ? phoneDigits.trim() : undefined;
        const results = await Promise.allSettled(
          selectedSlotIds.map((slotId) =>
            withOneRetryOnRejection(() =>
              registerForActivityFromSession({
                activityId: activity.id,
                timeSlotId: slotId,
                landingPageId,
                dietaryRestrictions: activity.collect_dietary_restrictions ? dietaryRestrictions : undefined,
                customFields: Object.keys(customFieldValues).length > 0 ? customFieldValues : undefined,
                phoneNumber: phoneOverride,
                countryDialCodeId: phoneOverride ? countryDialCodeId : undefined,
                suppressEmail: isMultiSlot,
                guestNames: guestNamesPayload,
              })
            )
          )
        );
        applyVerdict(tallyRegisterResults(results), results);
        // Fire-and-forget the multi-slot confirmation email — it already
        // handles its own errors internally, and awaiting it here blocked
        // the success view from rendering until the email request settled.
        void sendConsolidatedEmailsForBatch(results, isMultiSlot, activity.id);
        return;
      }

      // Anonymous public-page path: identity comes from the form.
      const formattedPhone = toE164(phoneDigits, countryDialCodeId) || phoneDigits.trim();

      if (mode === "waitlist") {
        const result = await joinActivityWaitlist({
          activityId: activity.id,
          timeSlotId: selectedSlotIds[0]!,
          firstName,
          lastName,
          email,
          phoneNumber: formattedPhone,
          countryDialCodeId,
          landingPageId,
          guestNames: guestNamesPayload,
        });

        if (result.success) {
          if (eventId) saveAttendeePrefill(eventId, identityToRemember);
          setModalState("success");
          onSuccess({ activityId: activity.id, addedSlotIds: [selectedSlotIds[0]!], cancelledSlotIds: [] });
        } else {
          setErrorMessage(result.error || "Failed to join waitlist");
          setModalState("error");
        }
        return;
      }

      const results = await Promise.allSettled(
        selectedSlotIds.map((slotId) =>
          withOneRetryOnRejection(() =>
            registerForActivity({
              activityId: activity.id,
              timeSlotId: slotId,
              firstName,
              lastName,
              email,
              phoneNumber: formattedPhone,
              countryDialCodeId,
              dietaryRestrictions: activity.collect_dietary_restrictions ? dietaryRestrictions : undefined,
              customFields: Object.keys(customFieldValues).length > 0 ? customFieldValues : undefined,
              landingPageId,
              suppressEmail: isMultiSlot,
              guestNames: guestNamesPayload,
            })
          )
        )
      );
      applyVerdict(tallyRegisterResults(results), results);
      void sendConsolidatedEmailsForBatch(results, isMultiSlot, activity.id);

      function applyVerdict(
        { registered, waitlisted, failureCount, firstError, firstRejection }: RegisterVerdict,
        results: RegisterPromiseResult[]
      ) {
        const succeededSlotIds = succeededSlotIdsFrom(results, selectedSlotIds);
        if (registered === 0 && waitlisted === 0) {
          if (!firstError && firstRejection !== undefined) {
            // The request never got a server-returned answer (network drop/timeout surviving the
            // retry) rather than being rejected by the server — capture it, since this class of
            // failure was previously invisible everywhere (no console log, no analytics event).
            console.error("ActivitySignupModal: registration request never completed", firstRejection);
            posthog?.capture(REGISTRATION_CLIENT_FAILURE_EVENT, {
              activity_id: activity.id,
              slot_ids: selectedSlotIds,
              reason: firstRejection instanceof Error ? firstRejection.message : String(firstRejection),
            });
            setErrorMessage(REGISTRATION_CONNECTION_ISSUE_MESSAGE);
          } else {
            setErrorMessage(firstError || "Failed to register. Please try again.");
          }
          setModalState("error");
          return;
        }
        // Only the anonymous path captures real form values; for logged-in
        // users the inputs stay hidden + empty, so persisting would clobber
        // any existing prefill with blanks.
        if (!isLoggedIn && eventId) saveAttendeePrefill(eventId, identityToRemember);
        if (failureCount > 0) {
          setPartialFailure({
            succeeded: registered + waitlisted,
            total: selectedSlotIds.length,
            reason: firstError || "One or more slots couldn't be reserved.",
          });
        }
        if (selectedSlotIds.length > 1) {
          setMultiSlotBreakdown({ registered, waitlisted });
        }
        if (waitlisted > 0 && registered === 0) setMode("waitlist");

        const calendar = pickSingleSlotCalendarInfo(results, registered);
        if (calendar) setCalendarSlotInfo(calendar);

        setModalState("success");
        onSuccess({ activityId: activity.id, addedSlotIds: succeededSlotIds, cancelledSlotIds: [] });
      }
    },
    [
      selectedSlotIds,
      mode,
      isLoggedIn,
      viewer,
      needsIdentityInputs,
      needsPhoneInput,
      firstName,
      lastName,
      email,
      phoneDigits,
      countryDialCodeId,
      dietaryRestrictions,
      customFieldValues,
      customFields,
      guestCount,
      guestNames,
      activity,
      landingPageId,
      eventId,
      onSuccess,
      canManage,
      hasChanges,
      guestsChanged,
      toAddSlotIds,
      toCancelSlotIds,
      guestUpdateSlotIds,
      runReconcile,
    ]
  );

  const submitDisabled = canManage
    ? isReconciling || !hasChanges
    : modalState === "submitting" ||
      selectedSlotIds.length === 0 ||
      (needsIdentityInputs && (!firstName.trim() || !lastName.trim() || !email.trim())) ||
      (needsPhoneInput && !phoneDigits.trim());

  // The submit button shares its label ("Sign up") with the card the visitor just tapped, and
  // goes disabled until a slot is picked — with nothing on screen saying why. Show a hint next to
  // it in exactly that case (a slot is available to pick, none selected, on the public signup path)
  // so the greyed-out button explains itself.
  const showSelectSlotHint =
    !canManage && selectedSlotIds.length === 0 && availableSlots.length > 0 && modalState !== "submitting";

  // Render
  return (
    <>
      <FetchModal
        isOpen
        onClose={onClose}
        title={modalState === "success" ? undefined : activity.title}
        subtitle={
          modalState === "success" || !activity.meeting_location ? undefined : (
            <span className="inline-flex items-center gap-1.5">
              <MapPin className="h-4 w-4 shrink-0" aria-hidden="true" />
              {activity.meeting_location}
            </span>
          )
        }
        size="auto"
        className={
          modalState === "success"
            ? "mx-4 my-8 h-auto w-full max-w-sm rounded-3xl p-6"
            : "m-0 h-[100dvh] max-h-[100dvh] w-screen rounded-none p-4 sm:my-8 sm:h-auto sm:max-h-[90vh] sm:w-full sm:max-w-xl sm:rounded-3xl sm:p-6"
        }
        contentClassName="overflow-y-auto overscroll-contain px-1"
      >
        {modalState === "success" ? (
          <SignupSuccessView
            registration={{
              mode,
              activityTitle: activity.title,
              confirmationMessage: activity.confirmation_message,
              email: viewer?.email ?? email,
              multiSlotBreakdown,
              partialFailure,
            }}
            slotInfo={calendarSlotInfo}
            onDone={onClose}
          />
        ) : allSlotsAlreadyRegistered && mode === "register" && !canManage ? (
          <div className="flex flex-col items-center gap-4 py-6 text-center">
            <CheckCircle2 className="h-10 w-10 text-emerald-500" aria-hidden="true" />
            <div className="flex flex-col gap-1">
              <p className="text-base font-semibold text-gray-900">You&apos;re already signed up</p>
              <p className="text-sm font-medium text-gray-500">You&apos;re registered for {activity.title}.</p>
            </div>
            {registeredSlotLabels.length > 0 && (
              <ul className="flex w-full flex-col gap-1.5">
                {registeredSlotLabels.map((label) => (
                  <li
                    key={label}
                    className="flex items-center gap-2 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm font-medium text-gray-700"
                  >
                    <Calendar className="h-4 w-4 shrink-0 text-gray-400" aria-hidden="true" />
                    {label}
                  </li>
                ))}
              </ul>
            )}
            <button
              type="button"
              onClick={onClose}
              className="touch-manipulation rounded-lg px-4 py-2.5 text-sm font-medium transition-opacity [@media(hover:hover)]:hover:opacity-90"
              style={{ backgroundColor: primaryColor, color: buttonTextColor }}
            >
              Done
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="flex flex-col gap-5">
            {activity.description && (
              <SanitizedHtml html={activity.description} className="prose prose-sm max-w-none text-gray-700" />
            )}

            {/* Error banner */}
            {modalState === "error" && errorMessage && (
              <div className="flex items-start gap-2 rounded-lg bg-red-50 p-3" role="alert">
                <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-red-500" aria-hidden="true" />
                <p className="text-sm font-medium text-red-700">{errorMessage}</p>
              </div>
            )}

            {/* All slots full banner — not in manage mode, where the viewer already holds seats. */}
            {allSlotsFull && mode === "register" && !canManage && (
              <div className="flex flex-col gap-2 rounded-lg bg-amber-50 p-3">
                <p className="text-sm font-medium text-amber-800">All time slots are full.</p>
                <button
                  type="button"
                  onClick={() => {
                    setMode("waitlist");
                    setSelectedSlotIds([]);
                  }}
                  className="touch-manipulation text-sm font-medium underline"
                  style={{ color: primaryColor }}
                >
                  Join the waitlist instead
                </button>
              </div>
            )}

            {/* Day-cap/exclusion-group banner — every bookable slot is blocked by the rule. The time
                slot list below is still shown (each row disabled), so this is purely the "why".
                Not in manage mode (editing an existing registration) or waitlist mode — the rule
                only ever applies to a fresh registered signup, same as the per-slot hint below. */}
            {ruleBlock?.message && mode === "register" && !canManage && (
              <div className="flex items-start gap-2 rounded-lg bg-red-50 p-3" role="alert">
                <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-red-500" aria-hidden="true" />
                <p className="text-sm font-medium text-red-700">{ruleBlock.message}</p>
              </div>
            )}

            {/* Time slot selection */}
            {availableSlots.length > 0 && (
              <TimeSlotPicker
                slots={availableSlots}
                slotStates={slotStates}
                selectedIds={selectedSlotIds}
                onToggle={handleToggleSlot}
                primaryColor={primaryColor}
                mode={mode}
                multi={isMultiSelect}
                legend={
                  mode === "waitlist"
                    ? "Select a time slot to waitlist"
                    : canManage
                      ? isMultiSelect
                        ? "Update your time slots"
                        : "Your time"
                      : isMultiSelect
                        ? "Select one or more time slots"
                        : "Select a time slot"
                }
              />
            )}

            {/* Identity inputs — anonymous public-page path only */}
            {needsIdentityInputs && (
              <>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <div className="flex flex-col gap-1">
                    <label htmlFor="signup-first-name" className="text-sm font-medium text-gray-700">
                      First Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      id="signup-first-name"
                      type="text"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      placeholder="First name"
                      required
                      className="rounded-lg border border-gray-200 px-3 py-2 text-base font-medium text-gray-900 outline-none transition-colors placeholder:text-gray-400 focus:border-indigo-300 focus:ring-1 focus:ring-indigo-300 sm:text-sm"
                    />
                  </div>
                  <div className="flex flex-col gap-1">
                    <label htmlFor="signup-last-name" className="text-sm font-medium text-gray-700">
                      Last Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      id="signup-last-name"
                      type="text"
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      placeholder="Last name"
                      required
                      className="rounded-lg border border-gray-200 px-3 py-2 text-base font-medium text-gray-900 outline-none transition-colors placeholder:text-gray-400 focus:border-indigo-300 focus:ring-1 focus:ring-indigo-300 sm:text-sm"
                    />
                  </div>
                </div>

                <div className="flex flex-col gap-1">
                  <label htmlFor="signup-email" className="text-sm font-medium text-gray-700">
                    Email <span className="text-red-500">*</span>
                  </label>
                  <input
                    id="signup-email"
                    type="email"
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      if (emailError) setEmailError(null);
                    }}
                    placeholder="your@email.com"
                    required
                    aria-invalid={!!emailError}
                    aria-describedby={emailError ? "signup-email-error" : undefined}
                    className={`rounded-lg border px-3 py-2 text-base font-medium text-gray-900 outline-none transition-colors placeholder:text-gray-400 focus:border-indigo-300 focus:ring-1 focus:ring-indigo-300 sm:text-sm ${emailError ? "border-red-300" : "border-gray-200"}`}
                  />
                  {emailError && (
                    <p id="signup-email-error" role="alert" className="text-xs font-medium text-red-500">
                      {emailError}
                    </p>
                  )}
                </div>
              </>
            )}

            {/* Phone Number — shown for anonymous OR for logged-in viewers without a phone on file */}
            {needsPhoneInput && (
              <div className="flex flex-col gap-1">
                <label htmlFor="signup-phone" className="text-sm font-medium text-gray-700">
                  Phone Number <span className="text-red-500">*</span>
                </label>
                <PhoneNumberInput
                  id="signup-phone"
                  value={phoneDigits}
                  onChange={(val) => {
                    setPhoneDigits(val);
                    if (phoneError) setPhoneError(null);
                  }}
                  countryDialCodeId={countryDialCodeId}
                  onCountryDialCodeIdChange={setCountryDialCodeId}
                  required
                />
                {phoneError && (
                  <p id="signup-phone-error" role="alert" className="text-xs font-medium text-red-500">
                    {phoneError}
                  </p>
                )}
              </div>
            )}

            {/* Dietary restrictions */}
            {activity.collect_dietary_restrictions && mode === "register" && (
              <div className="flex flex-col gap-1">
                <label htmlFor="signup-dietary" className="text-sm font-medium text-gray-700">
                  Dietary Restrictions
                </label>
                <input
                  id="signup-dietary"
                  type="text"
                  value={dietaryRestrictions}
                  onChange={(e) => setDietaryRestrictions(e.target.value)}
                  placeholder="e.g., Vegetarian, Gluten-free, Allergies"
                  className="rounded-lg border border-gray-200 px-3 py-2 text-base font-medium text-gray-900 outline-none transition-colors placeholder:text-gray-400 focus:border-indigo-300 focus:ring-1 focus:ring-indigo-300 sm:text-sm"
                />
              </div>
            )}

            {/* Custom fields */}
            {mode === "register" &&
              customFields.map((field) => (
                <CustomFieldInput
                  key={field.id}
                  field={field}
                  value={customFieldValues[field.id]}
                  onChange={(val) => handleCustomFieldChange(field.id, val)}
                />
              ))}

            {/* +1 guests. In manage mode the control is held back until the viewer's existing guests
                load, so an unseeded form can't be confirmed into a guest wipe. */}
            {activity.allow_plus_one &&
              (needsGuestSeed && guestSeedStatus !== "loaded" ? (
                guestSeedStatus === "error" ? (
                  <p role="alert" className="text-sm font-medium text-red-600">
                    Couldn&apos;t load your current guests. Close and reopen to edit them.
                  </p>
                ) : (
                  <p className="text-sm font-medium text-gray-400" aria-live="polite" aria-busy="true">
                    Loading your guests…
                  </p>
                )
              ) : (
                <PlusOneGuestsInput
                  maxGuests={activity.max_guests_per_signup}
                  count={guestCount}
                  names={guestNames}
                  onCountChange={setGuestCount}
                  onNameChange={handleGuestNameChange}
                  showErrors={guestErrorsShown}
                  warning={wontFitWarning}
                />
              ))}

            {/* Submit button — paired with a hint that explains the disabled state when no slot
                is selected yet, so the greyed-out button isn't a dead end. */}
            <div className="flex flex-col gap-2">
              <button
                type="submit"
                disabled={submitDisabled}
                className="touch-manipulation rounded-lg px-4 py-2.5 text-sm font-medium transition-opacity [@media(hover:hover)]:hover:opacity-90 disabled:opacity-50"
                style={{ backgroundColor: primaryColor, color: buttonTextColor }}
              >
                {canManage
                  ? isReconciling
                    ? "Saving..."
                    : "Confirm"
                  : getSubmitLabel({ modalState, mode, count: selectedSlotIds.length, isMultiSelect })}
              </button>
              {showSelectSlotHint && (
                <p className="text-center text-xs font-medium text-gray-500" aria-live="polite">
                  {mode === "waitlist" ? "Select a time slot to join the waitlist" : "Select a time slot to continue"}
                </p>
              )}
            </div>

            {/* Cancel link — manage path only. Drops every registered slot via the REVIEW step. */}
            {canManage && (
              <button
                type="button"
                onClick={handleCancelAll}
                disabled={isReconciling}
                className="touch-manipulation text-center text-sm font-semibold text-red-600 underline transition-colors [@media(hover:hover)]:hover:text-red-700 disabled:opacity-50"
              >
                {registeredSlotIds.size > 1 ? "Cancel all my registrations" : "Cancel registration"}
              </button>
            )}

            {/* Switch to waitlist mode — not in manage mode (editing existing registrations). */}
            {mode === "register" &&
              !canManage &&
              !allSlotsFull &&
              bookableSlots.some((s) => {
                const counts = slotCounts[s.id];
                return counts && s.capacity !== null && counts.registered >= s.capacity;
              }) && (
                <button
                  type="button"
                  onClick={() => {
                    setMode("waitlist");
                    setSelectedSlotIds([]);
                  }}
                  className="touch-manipulation text-center text-sm font-medium text-gray-500 underline [@media(hover:hover)]:hover:text-gray-700"
                >
                  Or join the waitlist for full time slots
                </button>
              )}
          </form>
        )}
      </FetchModal>

      {modalState === "review" && (
        <ConfirmChangesModal
          activityTitle={activity.title}
          toAdd={toAddLabels}
          toCancel={toCancelLabels}
          primaryColor={primaryColor}
          isSubmitting={isReconciling}
          onBack={() => setModalState("form")}
          onConfirm={() => {
            void runReconcile(toAddSlotIds, toCancelSlotIds, guestUpdateSlotIds);
          }}
        />
      )}
    </>
  );
}

function getSubmitLabel({
  modalState,
  mode,
  count,
  isMultiSelect,
}: {
  modalState: ModalState;
  mode: SignupMode;
  count: number;
  isMultiSelect: boolean;
}): string {
  if (modalState === "submitting") return "Submitting...";
  if (mode === "waitlist") return "Join Waitlist";
  if (isMultiSelect && count > 1) return `Sign up for ${count} slots`;
  return "Sign Up";
}
